import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

// Translations for the app
const translations = {
  fr: {
    // Landing / Splash Screen
    welcome: "Bienvenue",
    chooseLanguage: "Choisissez votre langue d'apprentissage",
    french: "Français",
    english: "Anglais",
    continue: "Continuer",
    
    // Navigation
    dashboard: "Accueil",
    vocabulary: "Vocabulaire",
    grammar: "Grammaire",
    situations: "Situations",
    chat: "Chatbot",
    admin: "Admin",
    logout: "Déconnexion",
    back: "Retour",
    
    // Levels
    level: "Niveau",
    beginner: "Débutant",
    intermediate: "Intermédiaire",
    advanced: "Avancé",
    locked: "Verrouillé",
    unlocked: "Déverrouillé",
    completed: "Terminé",
    
    // Sounds Page
    soundsTitle: "Les Sons de la Darija",
    soundsSubtitle: "Module d'Introduction - À maîtriser avant de commencer",
    soundsIntroTitle: "Pourquoi c'est important ?",
    soundsIntroText: "Le Darija marocain utilise des sons qui n'existent pas en français ou en anglais. Avant d'apprendre vos premiers mots, il est essentiel de comprendre comment prononcer ces lettres spéciales. Prenez votre temps pour les écouter et les pratiquer !",
    phonetic: "Phonétique",
    examples: "Exemples",
    understood: "J'ai compris les sons de la Darija et je suis prêt(e) à commencer mon apprentissage",
    continueToLevel: "Continuer vers le Niveau 1",
    soundsTip: "Conseil : Pratiquez ces sons régulièrement. Vous pouvez revenir à cette page à tout moment !",
    
    // Dashboard
    hello: "Marhaba",
    chooseLevel: "Choisissez un niveau pour commencer",
    yourStats: "Vos statistiques",
    words: "Mots",
    lessons: "Leçons",
    modules: "Modules",
    progress: "Progression",
    startHere: "Commencez ici",
    requiredScore: "Score requis : 80%",
    
    // Chat
    chatTitle: "Chatbot Darija",
    chatPlaceholder: "Écrivez votre message en Darija, français ou anglais...",
    chatWelcome: "Marhaba! Je suis votre assistant pour pratiquer le Darija. N'hésitez pas à me poser des questions ou à pratiquer des conversations !",
    sendError: "Erreur lors de l'envoi du message",
    
    // General
    loading: "Chargement...",
    error: "Erreur",
    save: "Enregistrer",
    cancel: "Annuler",
    edit: "Modifier",
    delete: "Supprimer",
    confirm: "Confirmer",
    
    // Script modes
    phoneticMode: "Mode Phonétique",
    arabicMode: "Mode Arabe",
    scriptToggle: "Changer l'écriture"
  },
  en: {
    // Landing / Splash Screen
    welcome: "Welcome",
    chooseLanguage: "Choose your learning language",
    french: "French",
    english: "English",
    continue: "Continue",
    
    // Navigation
    dashboard: "Home",
    vocabulary: "Vocabulary",
    grammar: "Grammar",
    situations: "Situations",
    chat: "Chatbot",
    admin: "Admin",
    logout: "Logout",
    back: "Back",
    
    // Levels
    level: "Level",
    beginner: "Beginner",
    intermediate: "Intermediate",
    advanced: "Advanced",
    locked: "Locked",
    unlocked: "Unlocked",
    completed: "Completed",
    
    // Sounds Page
    soundsTitle: "Darija Sounds",
    soundsSubtitle: "Introduction Module - Master before starting",
    soundsIntroTitle: "Why is this important?",
    soundsIntroText: "Moroccan Darija uses sounds that don't exist in French or English. Before learning your first words, it's essential to understand how to pronounce these special letters. Take your time to listen and practice!",
    phonetic: "Phonetic",
    examples: "Examples",
    understood: "I understand the Darija sounds and I'm ready to start learning",
    continueToLevel: "Continue to Level 1",
    soundsTip: "Tip: Practice these sounds regularly. You can come back to this page anytime!",
    
    // Dashboard
    hello: "Marhaba",
    chooseLevel: "Choose a level to start",
    yourStats: "Your statistics",
    words: "Words",
    lessons: "Lessons",
    modules: "Modules",
    progress: "Progress",
    startHere: "Start here",
    requiredScore: "Required score: 80%",
    
    // Chat
    chatTitle: "Darija Chatbot",
    chatPlaceholder: "Write your message in Darija, French or English...",
    chatWelcome: "Marhaba! I'm your assistant to practice Darija. Feel free to ask me questions or practice conversations!",
    sendError: "Error sending message",
    
    // General
    loading: "Loading...",
    error: "Error",
    save: "Save",
    cancel: "Cancel",
    edit: "Edit",
    delete: "Delete",
    confirm: "Confirm",
    
    // Script modes
    phoneticMode: "Phonetic Mode",
    arabicMode: "Arabic Mode",
    scriptToggle: "Switch script"
  }
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('appLanguage') || null;
  });

  useEffect(() => {
    if (language) {
      localStorage.setItem('appLanguage', language);
    }
  }, [language]);

  const t = (key) => {
    if (!language) return translations.fr[key] || key;
    return translations[language]?.[key] || translations.fr[key] || key;
  };

  const isLanguageSelected = () => {
    return language !== null;
  };

  const resetLanguage = () => {
    setLanguage(null);
    localStorage.removeItem('appLanguage');
  };

  return (
    <LanguageContext.Provider value={{ 
      language, 
      setLanguage, 
      t, 
      isLanguageSelected,
      resetLanguage 
    }}>
      {children}
    </LanguageContext.Provider>
  );
};
